import type { LinksFunction, MetaFunction } from '@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
} from '@remix-run/react';
import { MantineProvider, ColorSchemeScript, Container, Title, Text, Button, Stack } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { NavigationBar } from '~/components/NavigationBar';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@fontsource-variable/geist';
import '~/theme/style.css';
import { shadcnTheme } from '~/theme/theme';
import { shadcnCssVariableResolver } from '~/theme/cssVariableResolver';
import globalStyles from './styles/global.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: globalStyles },
  { rel: 'manifest', href: '/manifest.webmanifest' },
  { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
];

export const meta: MetaFunction = () => {
  return [
    { title: 'SOMTB - Bicycle Maintenance Tracker' },
    { name: 'description', content: 'Track your bicycle maintenance and service intervals with Strava integration' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'theme-color', content: '#ffffff', media: '(prefers-color-scheme: light)' },
    { name: 'theme-color', content: '#0c0a09', media: '(prefers-color-scheme: dark)' },
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-title', content: 'SOMTB' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
  ];
};

const providerProps = {
  theme: shadcnTheme,
  cssVariablesResolver: shadcnCssVariableResolver,
  defaultColorScheme: 'auto',
} as const;

export default function App() {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        <Meta />
        <Links />
        <ColorSchemeScript defaultColorScheme="auto" />
      </head>
      <body>
        <MantineProvider {...providerProps}>
          <DatesProvider settings={{ firstDayOfWeek: 0 }}>
            <NavigationBar />
            <Outlet />
          </DatesProvider>
        </MantineProvider>
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>
  );
}

// Rendered into the static SPA shell (build/client/index.html). Includes <Meta/> and
// <Links/> so the manifest and iOS home-screen tags are in the served HTML, not only
// added after hydration.
export function HydrateFallback() {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        <Meta />
        <Links />
        <ColorSchemeScript defaultColorScheme="auto" />
      </head>
      <body>
        <Scripts />
      </body>
    </html>
  );
}

// Global error boundary for unhandled errors
export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Error - SOMTB</title>
          <Meta />
          <Links />
          <ColorSchemeScript defaultColorScheme="auto" />
        </head>
        <body>
          <MantineProvider {...providerProps}>
            <Container size="sm" py="xl">
              <Stack gap="md" align="center">
                <Title order={1} c="red">
                  {error.status} {error.statusText}
                </Title>
                <Text size="lg" c="dimmed" ta="center">
                  {error.data || 'An error occurred while processing your request.'}
                </Text>
                <Button component="a" href="/" size="lg">
                  Return Home
                </Button>
              </Stack>
            </Container>
          </MantineProvider>
          <Scripts />
        </body>
      </html>
    );
  }

  // Handle unexpected errors
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - SOMTB</title>
        <Meta />
        <Links />
        <ColorSchemeScript defaultColorScheme="auto" />
      </head>
      <body>
        <MantineProvider {...providerProps}>
          <Container size="sm" py="xl">
            <Stack gap="md" align="center">
              <Title order={1} c="red">
                Unexpected Error
              </Title>
              <Text size="lg" c="dimmed" ta="center">
                {errorMessage}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Please try again later or contact support if the problem persists.
              </Text>
              <Button component="a" href="/" size="lg">
                Return Home
              </Button>
            </Stack>
          </Container>
        </MantineProvider>
        <Scripts />
      </body>
    </html>
  );
}
